import React, { useState, useEffect } from 'react';
import {
  Grid,
  Typography,
  Card,
  CardHeader,
  CardContent,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  CardActions,
  Button,
  IconButton,
  FormControlLabel,
  Checkbox,
  Box,
} from '@mui/material';
import { useApiGet, useApiSend } from '../../utils/httpClient';
import {
  getUserPermissions,
  updateUserPermissions,
  getUserRoles,
  getUserApplications,
} from '../../endpoints/usersTs';
import { getAllReports } from '../../endpoints/reportsTs';
import dayjs from 'dayjs';
import { roles, applications } from '../../utils/variables';
import { AddCircle, Delete, Edit, Save } from '@mui/icons-material';
import SPDSelector from '../../Components/SPDSelector';
import ChapterSelector from '../../Components/ChapterSelector';

export default function UserPermissions({ id }) {
  const [isSaving, setIsSaving] = useState(false);
  const [errorSaving, setErrorSaving] = useState(null);
  const [editRoles, setEditRoles] = useState(false);
  const [editRoleId, setEditRoleId] = useState(null);
  const [imisAuth, setImisAuth] = useState(false);
  const [isActive, setIsActive] = useState(false);
  // applications
  const [editApplications, setEditApplications] = useState(false);
  const [removedApplications, setRemovedApplications] = useState([]);
  const [addedApplications, setAddedApplications] = useState([]);
  const [addedApplicationNames, setAddedApplicationNames] = useState([]);
  const [applicationId, setApplicationId] = useState('');
  //reports
  const [editReports, setEditReports] = useState(false);
  const [removedReports, setRemovedReports] = useState([]);
  const [addedReports, setAddedReports] = useState([]);
  const [addedReportNames, setAddedReportNames] = useState([]);
  const [reportId, setReportId] = useState('');
  const [reportScope, setReportScope] = useState('international'); // 'international', 'spd_chapter', 'local_chapter'
  const [reportIsInternational, setReportIsInternational] = useState(0);
  const [reportChapterId, setReportChapterId] = useState('');
  const [reportChapterObject, setReportChapterObject] = useState(null);
  // local chapters
  const [editLocalChapters, setEditLocalChapters] = useState(false);
  const [removedLocalChapters, setRemovedLocalChapters] = useState([]);
  const [addedLocalChapters, setAddedLocalChapters] = useState([]);
  const [addedLocalChapterNames, setAddedLocalChapterNames] = useState([]);
  const [localChapterId, setLocalChapterId] = useState('');
  const [localChapterObject, setLocalChapterObject] = useState(null);
  // SPD chapters
  const [editSPDChapters, setEditSPDChapters] = useState(false);
  const [removedSPDChapters, setRemovedSPDChapters] = useState([]);
  const [addedSPDChapters, setAddedSPDChapters] = useState([]);
  const [addedSPDChapterNames, setAddedSPDChapterNames] = useState([]);
  const [spdChapterId, setSpdChapterId] = useState('');
  const [spdChapterObject, setSpdChapterObject] = useState(null);
  // Don't reset these states
  const [filteredApplications, setFilteredApplications] = useState([]);
  const [filteredReports, setFilteredReports] = useState([]);

  const resetState = () => {
    setIsSaving(false);
    setErrorSaving(null);
    setEditRoles(false);
    setEditRoleId(null);
    setImisAuth(false);
    setIsActive(false);
    // Reset the applications
    setEditApplications(false);
    setRemovedApplications([]);
    setAddedApplications([]);
    setAddedApplicationNames([]);
    setApplicationId('');
    // Reset the reports
    setEditReports(false);
    setRemovedReports([]);
    setAddedReports([]);
    setAddedReportNames([]);
    setReportId('');
    setReportScope('international');
    setReportIsInternational(0);
    setReportChapterId('');
    setReportChapterObject(null);
    // Reset the local chapters
    setEditLocalChapters(false);
    setRemovedLocalChapters([]);
    setAddedLocalChapters([]);
    setAddedLocalChapterNames([]);
    setLocalChapterId('');
    setLocalChapterObject(null);
    // Reset the SPD chapters
    setEditSPDChapters(false);
    setRemovedSPDChapters([]);
    setAddedSPDChapters([]);
    setAddedSPDChapterNames([]);
    setSpdChapterId('');
    setSpdChapterObject(null);
  };

  const {
    data: userPermissions,
    isLoading,
    refetch,
  } = useApiGet(
    ['userPermissions', id],
    getUserPermissions,
    {
      enabled: !!id,
    },
    { id }
  );
  const { data: userRoles } = useApiGet(
    ['userRoles'],
    getUserRoles,
    {
      enabled: editRoles,
    },
    {}
  );
  const { data: userApplications } = useApiGet(
    ['userApplications'],
    getUserApplications,
    {
      enabled: editApplications,
    },
    {}
  );

  // Need to filter the applications based on the userPermissions
  // remove the applications that are already assigned to the user
  useEffect(() => {
    if (userApplications) {
      const filtered = userApplications.filter(
        (app) =>
          !userPermissions.applications.some(
            (userApp) => userApp.application_id === app.id
          )
      );
      setFilteredApplications(filtered);
    }
  }, [userApplications, userPermissions]);
  const { data: userReports } = useApiGet(
    ['allReports'],
    getAllReports,
    {
      enabled: editReports,
    },
    {}
  );

  // Need to filter the reports based on the userPermissions
  // Note: Same report can be assigned with different scopes, so we allow duplicates
  // but filter out reports that are already in the addedReports list for this edit session
  useEffect(() => {
    if (userReports) {
      const addedReportIds = new Set(addedReports.map((r) => r.report_id));
      const filtered = userReports.filter(
        (report) => !addedReportIds.has(report.id)
      );
      setFilteredReports(filtered);
    }
  }, [userReports, addedReports]);

  const { mutate: updatePermissions } = useApiSend(
    updateUserPermissions,
    (data) => {
      setIsSaving(false);
      resetState();
      refetch();
    },
    (error) => {
      setErrorSaving(
        error.message
          ? error.message
          : 'Error submitting request, if problem persists, please contact support'
      );
      setIsSaving(false);
    }
  );

  const handleEditRole = () => {
    setEditRoleId(userPermissions.role_id ? userPermissions.role_id : 'none');
    setEditRoles(true);
    setImisAuth(userPermissions.imis_auth);
    setIsActive(userPermissions.is_active);
  };

  const handleCancelRoleEdit = () => {
    setEditRoles(false);
    setEditRoleId(null);
    setImisAuth(false);
    setIsActive(false);
  };

  const handleSaveRoleEdit = () => {
    setIsSaving(true);
    updatePermissions({
      id,
      role_id: editRoleId,
      imis_auth: imisAuth,
      is_active: isActive,
    });
  };

  const canEdit = () => {
    let userRole = localStorage.getItem('role');
    let acceptableRoles = [roles.Admin];
    if (acceptableRoles.includes(userRole)) {
      return true;
    } else {
      let userApps = localStorage.getItem('apps');
      if (userApps.includes(applications.PermissionsAdmin)) {
        return true;
      }
      return false;
    }
  };

  // Handle the applications edit
  const handleAddApplication = () => {
    // We need to add both the applicationId and the application name to the addedApplications array
    const application = userApplications.find(
      (app) => app.id === applicationId
    );
    if (application) {
      setAddedApplications([...addedApplications, applicationId]);
      setAddedApplicationNames([...addedApplicationNames, application.name]);
    }
    setApplicationId('');
  };

  const cancelApplicationEdit = () => {
    setEditApplications(false);
    setRemovedApplications([]);
    setAddedApplications([]);
    setAddedApplicationNames([]);
    setApplicationId('');
  };

  const handleSaveApplicationEdit = () => {
    setIsSaving(true);
    updatePermissions({
      id,
      removed_applications: removedApplications,
      added_applications: addedApplications,
    });
  };

  // Handle the reports edit
  const handleAddReport = () => {
    // We need to add the report with scope information
    const report = userReports.find((report) => report.id === reportId);
    if (report) {
      // Build scope display name
      let scopeName = '';
      if (reportScope === 'international') {
        scopeName = 'International';
      } else if (reportScope === 'spd_chapter' && reportChapterObject) {
        scopeName = `SPD: ${reportChapterObject.name}`;
      } else if (reportScope === 'local_chapter' && reportChapterObject) {
        scopeName = `Chapter: ${reportChapterObject.name}`;
      }

      // For reports, we send report_id, group_type_id (scope), and is_international
      // The backend will create the permission with source_id = report_id
      // If chapter/SPD is selected, it will be stored based on group_type_id
      const reportWithScope = {
        report_id: reportId,
        group_type_id: reportScope, // Scope: 'international', 'spd_chapter', or 'local_chapter'
        is_international: reportIsInternational,
        // Include chapter/SPD ID if scope requires it
        ...(reportScope === 'spd_chapter' && reportChapterId
          ? { spd_chapter_id: reportChapterId }
          : {}),
        ...(reportScope === 'local_chapter' && reportChapterId
          ? { local_chapter_id: reportChapterId }
          : {}),
      };

      setAddedReports([...addedReports, reportWithScope]);
      setAddedReportNames([
        ...addedReportNames,
        `${report.name}${scopeName ? ` (${scopeName})` : ''}`,
      ]);

      // Reset form
      setReportId('');
      setReportScope('international');
      setReportIsInternational(0);
      setReportChapterId('');
      setReportChapterObject(null);
    }
  };
  const cancelReportEdit = () => {
    setEditReports(false);
    setRemovedReports([]);
    setAddedReports([]);
    setAddedReportNames([]);
    setReportId('');
    setReportScope('international');
    setReportIsInternational(0);
    setReportChapterId('');
    setReportChapterObject(null);
  };
  const handleSaveReportEdit = () => {
    setIsSaving(true);
    // removed_reports should be permission record IDs (not report IDs)
    // added_reports should be objects with report_id, group_type_id, is_international
    updatePermissions({
      id,
      removed_reports: removedReports, // These are permission IDs
      added_reports: addedReports, // These are objects with report_id, group_type_id, is_international
    });
  };

  // Handle the local chapters edit
  const handleSelectLocalChapter = (localChapter) => {
    setLocalChapterObject(localChapter);
    setLocalChapterId(localChapter.id);
  };
  const handleAddLocalChapter = () => {
    if (localChapterObject) {
      setAddedLocalChapters([...addedLocalChapters, localChapterObject.id]);
      setAddedLocalChapterNames([
        ...addedLocalChapterNames,
        localChapterObject.name,
      ]);
    }
    setLocalChapterId('');
    setLocalChapterObject(null);
  };

  const cancelLocalChapterEdit = () => {
    setEditLocalChapters(false);
    setRemovedLocalChapters([]);
    setAddedLocalChapters([]);
    setAddedLocalChapterNames([]);
    setLocalChapterId('');
    setLocalChapterObject(null);
  };
  const handleSaveLocalChapterEdit = () => {
    setIsSaving(true);
    updatePermissions({
      id,
      removed_local_chapters: removedLocalChapters,
      added_local_chapters: addedLocalChapters,
    });
  };
  // Handle the SPD chapters edit
  const handleSelectSpdChapter = (spdChapter) => {
    setSpdChapterObject(spdChapter);
    setSpdChapterId(spdChapter.id);
  };
  const handleAddSpdChapter = () => {
    if (spdChapterObject) {
      setAddedSPDChapters([...addedSPDChapters, spdChapterObject.id]);
      setAddedSPDChapterNames([...addedSPDChapterNames, spdChapterObject.name]);
    }
    setSpdChapterId('');
    setSpdChapterObject(null);
  };
  const cancelSpdChapterEdit = () => {
    setEditSPDChapters(false);
    setRemovedSPDChapters([]);
    setAddedSPDChapters([]);
    setAddedSPDChapterNames([]);
    setSpdChapterId('');
    setSpdChapterObject(null);
  };
  const handleSaveSpdChapterEdit = () => {
    setIsSaving(true);
    updatePermissions({
      id,
      removed_spd_chapters: removedSPDChapters,
      added_spd_chapters: addedSPDChapters,
    });
  };

  if (isLoading) {
    return (
      <Grid container>
        <Grid item xs={12} padding>
          <Typography variant="h6">Loading...</Typography>
        </Grid>
      </Grid>
    );
  }
  if (!userPermissions) {
    return (
      <Grid container>
        <Grid item xs={12} padding>
          <Typography variant="h6">Permissions not found</Typography>
        </Grid>
      </Grid>
    );
  }

  return (
    <Grid container>
      <Grid item xs={12} padding>
        <Card sx={{ p: 2, boxShadow: 3, borderRadius: 2 }}>
          <CardHeader title={`Authentication / Role`} sx={{ p: 1 }} />
          <CardContent sx={{ p: 1 }}>
            <Grid container>
              <Grid item xs={12} md={6}>
                <Typography variant="body1" component={'span'}>
                  <strong>Last Login:</strong>{' '}
                  {userPermissions.last_login
                    ? dayjs(userPermissions.last_login).format('MM/DD/YYYY')
                    : 'N/A'}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body1" component={'span'}>
                  <strong>iMIS ID:</strong>{' '}
                  {userPermissions.imis_id ? userPermissions.imis_id : 'N/A'}
                </Typography>
              </Grid>
              {editRoles ? (
                <Grid item container>
                  <Grid item xs={12} md={6} padding>
                    <FormControl fullWidth size={'small'}>
                      <InputLabel id="edit-field-label">Role</InputLabel>
                      <Select
                        labelId="edit-field-label"
                        label={'Role'}
                        value={editRoleId}
                        onChange={(event) => setEditRoleId(event.target.value)}
                      >
                        <MenuItem value={'none'}>No Role</MenuItem>
                        {userRoles?.map((role) => (
                          <MenuItem key={role.id} value={role.id}>
                            {role.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={6} padding>
                    <FormControl fullWidth size={'small'}>
                      <InputLabel id="edit-field-label">iMIS Auth</InputLabel>
                      <Select
                        labelId="edit-field-label"
                        label={'iMIS Auth'}
                        value={imisAuth}
                        onChange={(event) => setImisAuth(event.target.value)}
                      >
                        <MenuItem value={true}>Yes</MenuItem>
                        <MenuItem value={false}>No</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={6} padding>
                    <FormControl fullWidth size={'small'} padding>
                      <InputLabel id="edit-field-label">Is Active</InputLabel>
                      <Select
                        labelId="edit-field-label"
                        label={'Is Active'}
                        value={isActive}
                        onChange={(event) => setIsActive(event.target.value)}
                      >
                        <MenuItem value={true}>Yes</MenuItem>
                        <MenuItem value={false}>No</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              ) : (
                <Grid item container xs={12}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="body1" component={'span'}>
                      <strong>Role:</strong>{' '}
                      {userPermissions.role_name
                        ? userPermissions.role_name
                        : 'N/A'}
                    </Typography>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Typography variant="body1" component={'span'}>
                      <strong>Is Active?:</strong>{' '}
                      {userPermissions.is_active ? 'Yes' : 'No'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="body1" component={'span'}>
                      <strong>iMIS Auth?:</strong>{' '}
                      {userPermissions.imis_auth ? 'Yes' : 'No'}
                    </Typography>
                  </Grid>
                </Grid>
              )}
            </Grid>
          </CardContent>
          {canEdit() && (
            <CardActions>
              {editRoles ? (
                <Grid container>
                  <Grid item xs={12}>
                    <Button
                      sx={{ marginRight: 1, fontWeight: 'bold' }}
                      variant="contained"
                      size={'small'}
                      color={'warning'}
                      onClick={handleCancelRoleEdit}
                      disabled={isSaving}
                    >
                      Cancel
                    </Button>
                    <Button
                      sx={{ marginRight: 1, fontWeight: 'bold' }}
                      variant="contained"
                      size={'small'}
                      onClick={handleSaveRoleEdit}
                      disabled={isSaving}
                      color={'green'}
                    >
                      <Save sx={{ fontSize: 18, paddingRight: 1 }} />
                      {isSaving ? 'Saving...' : 'Save'}
                    </Button>
                  </Grid>
                </Grid>
              ) : (
                <Grid item xs={12}>
                  <Button
                    variant="contained"
                    size={'small'}
                    onClick={handleEditRole}
                  >
                    <Edit sx={{ fontSize: 18, paddingRight: 1 }} />
                    Edit Roles / Auth
                  </Button>
                </Grid>
              )}
              {errorSaving && editRoles && (
                <Grid item xs={12} padding>
                  <Typography variant="body1" color={'error'}>
                    {errorSaving}
                  </Typography>
                </Grid>
              )}
            </CardActions>
          )}
        </Card>
      </Grid>
      <Grid item xs={12} md={6} padding>
        <Card sx={{ p: 2, boxShadow: 3, borderRadius: 2 }}>
          <CardHeader title={`Applications`} sx={{ p: 1 }} />
          <CardContent sx={{ p: 1 }}>
            <Grid container>
              {userPermissions.applications.map((app) => (
                <Grid
                  item
                  xs={12}
                  key={app.id}
                  backgroundColor={
                    removedApplications.includes(app.application_id)
                      ? '#f44336'
                      : 'inherit'
                  }
                >
                  <Typography variant="body1" component={'span'}>
                    {app.name}
                    {canEdit() &&
                      editApplications &&
                      !!!removedApplications.includes(app.application_id) && (
                        <IconButton
                          size={'small'}
                          color={'error'}
                          onClick={() => {
                            setRemovedApplications([
                              ...removedApplications,
                              app.application_id,
                            ]);
                          }}
                        >
                          <Delete sx={{ fontSize: 18 }} />
                        </IconButton>
                      )}
                  </Typography>
                </Grid>
              ))}
              {addedApplicationNames.map((app) => (
                <Grid
                  item
                  xs={12}
                  padding
                  key={app}
                  backgroundColor={'#4caf50'}
                >
                  <Typography variant="body1" component={'span'}>
                    {app}
                  </Typography>
                </Grid>
              ))}
              {userPermissions.applications.length === 0 && (
                <Grid item xs={12} padding textAlign="center">
                  <Typography variant="body1">No applications</Typography>
                </Grid>
              )}
              {editApplications && (
                <Grid item container xs={12}>
                  <Grid item xs={10} paddingTop={1}>
                    <FormControl fullWidth size={'small'}>
                      <InputLabel id="edit-field-label">
                        Applications
                      </InputLabel>
                      <Select
                        labelId="edit-field-label"
                        label={'Applications'}
                        value={applicationId}
                        onChange={(event) =>
                          setApplicationId(event.target.value)
                        }
                      >
                        {filteredApplications.map((application) => (
                          <MenuItem key={application.id} value={application.id}>
                            {application.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  {applicationId && (
                    <Grid item xs={2} padding={1} textAlign={'center'}>
                      <IconButton
                        size={'small'}
                        onClick={() => {
                          handleAddApplication();
                        }}
                        disabled={isSaving}
                        color={'green'}
                      >
                        <AddCircle />
                      </IconButton>
                    </Grid>
                  )}
                </Grid>
              )}
            </Grid>
          </CardContent>
          {canEdit() && (
            <CardActions>
              {editApplications ? (
                <Grid container>
                  <Grid item xs={12}>
                    <Button
                      sx={{ marginRight: 1, fontWeight: 'bold' }}
                      variant="contained"
                      size={'small'}
                      color={'warning'}
                      onClick={cancelApplicationEdit}
                      disabled={isSaving}
                    >
                      Cancel
                    </Button>
                    <Button
                      sx={{ marginRight: 1, fontWeight: 'bold' }}
                      variant="contained"
                      size={'small'}
                      color={'green'}
                      onClick={handleSaveApplicationEdit}
                      disabled={
                        isSaving ||
                        (addedApplications.length === 0 &&
                          removedApplications.length === 0)
                      }
                    >
                      <Save sx={{ fontSize: 18, paddingRight: 1 }} />
                      {isSaving ? 'Saving...' : 'Save'}
                    </Button>
                  </Grid>
                </Grid>
              ) : (
                <Grid item xs={12}>
                  <Button
                    variant="contained"
                    size={'small'}
                    onClick={() => setEditApplications(true)}
                  >
                    <Edit sx={{ fontSize: 18, paddingRight: 1 }} />
                    Edit Applications
                  </Button>
                </Grid>
              )}
              {errorSaving && editApplications && (
                <Grid item xs={12} padding>
                  <Typography variant="body1" color={'error'}>
                    {errorSaving}
                  </Typography>
                </Grid>
              )}
            </CardActions>
          )}
        </Card>
      </Grid>
      <Grid item xs={12} md={6} padding>
        <Card sx={{ p: 2, boxShadow: 3, borderRadius: 2 }}>
          <CardHeader title={`Reports`} sx={{ p: 1 }} />
          <CardContent sx={{ p: 1 }}>
            <Grid container>
              {userPermissions.reports.map((report) => {
                // Build scope display string
                let scopeDisplay = '';
                if (report.group_type_name) {
                  scopeDisplay = report.group_type_name;
                }
                if (report.is_international === 1) {
                  scopeDisplay = scopeDisplay
                    ? `${scopeDisplay} (International)`
                    : 'International';
                }

                return (
                  <Grid
                    item
                    xs={12}
                    key={report.id}
                    backgroundColor={
                      removedReports.includes(report.id) ? '#f44336' : 'inherit'
                    }
                  >
                    <Typography variant="body1" component={'span'}>
                      {report.name}
                      {scopeDisplay && (
                        <Typography
                          variant="body2"
                          component={'span'}
                          sx={{ ml: 1, color: 'text.secondary' }}
                        >
                          ({scopeDisplay})
                        </Typography>
                      )}
                      {canEdit() &&
                        editReports &&
                        !!!removedReports.includes(report.id) && (
                          <IconButton
                            size={'small'}
                            color={'error'}
                            onClick={() => {
                              setRemovedReports([...removedReports, report.id]);
                            }}
                          >
                            <Delete sx={{ fontSize: 18 }} />
                          </IconButton>
                        )}
                    </Typography>
                  </Grid>
                );
              })}
              {userPermissions.reports.length === 0 && (
                <Grid item xs={12} padding textAlign="center">
                  <Typography variant="body1">No applications</Typography>
                </Grid>
              )}
              {addedReportNames.map((report) => (
                <Grid
                  item
                  xs={12}
                  padding
                  key={report}
                  backgroundColor={'#4caf50'}
                >
                  <Typography variant="body1" component={'span'}>
                    {report}
                  </Typography>
                </Grid>
              ))}
            </Grid>
            {editReports && (
              <Box sx={{ mt: 2 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <FormControl fullWidth size={'small'}>
                      <InputLabel id="edit-field-label">Reports</InputLabel>
                      <Select
                        labelId="edit-field-label"
                        label={'Reports'}
                        value={reportId}
                        onChange={(event) => setReportId(event.target.value)}
                      >
                        {filteredReports.map((report) => (
                          <MenuItem key={report.id} value={report.id}>
                            {report.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  {reportId && (
                    <>
                      <Grid item xs={12} sm={6}>
                        <FormControl fullWidth size={'small'}>
                          <InputLabel id="scope-label">Scope</InputLabel>
                          <Select
                            labelId="scope-label"
                            label="Scope"
                            value={reportScope}
                            onChange={(e) => {
                              setReportScope(e.target.value);
                              setReportChapterId('');
                              setReportChapterObject(null);
                            }}
                          >
                            <MenuItem value="international">
                              International
                            </MenuItem>
                            <MenuItem value="spd_chapter">SPD Chapter</MenuItem>
                            <MenuItem value="local_chapter">
                              Local Chapter
                            </MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={reportIsInternational === 1}
                              onChange={(e) =>
                                setReportIsInternational(
                                  e.target.checked ? 1 : 0
                                )
                              }
                            />
                          }
                          label="International"
                        />
                      </Grid>
                      {reportScope === 'spd_chapter' && (
                        <Grid item xs={12}>
                          <SPDSelector
                            spd={reportChapterId}
                            handleSPDSelect={(spdChapter) => {
                              setReportChapterObject(spdChapter);
                              setReportChapterId(spdChapter.id);
                            }}
                            keyId={'report-permissions'}
                            objectReturned={true}
                          />
                        </Grid>
                      )}
                      {reportScope === 'local_chapter' && (
                        <Grid item xs={12}>
                          <ChapterSelector
                            chapter={reportChapterId}
                            handleChapterSelect={(localChapter) => {
                              setReportChapterObject(localChapter);
                              setReportChapterId(localChapter.id);
                            }}
                            keyId={'report-permissions'}
                            objectReturned={true}
                          />
                        </Grid>
                      )}
                      <Grid item xs={12}>
                        <Button
                          variant="contained"
                          size={'small'}
                          color={'primary'}
                          onClick={handleAddReport}
                          disabled={isSaving}
                          startIcon={<AddCircle />}
                          fullWidth
                        >
                          Add Report
                        </Button>
                      </Grid>
                    </>
                  )}
                </Grid>
              </Box>
            )}
          </CardContent>
          {canEdit() && (
            <CardActions>
              {editReports ? (
                <Grid container>
                  <Grid item xs={12}>
                    <Button
                      sx={{ marginRight: 1, fontWeight: 'bold' }}
                      variant="contained"
                      size={'small'}
                      color={'warning'}
                      onClick={cancelReportEdit}
                      disabled={isSaving}
                    >
                      Cancel
                    </Button>
                    <Button
                      sx={{ marginRight: 1, fontWeight: 'bold' }}
                      variant="contained"
                      size={'small'}
                      color={'green'}
                      onClick={handleSaveReportEdit}
                      disabled={
                        isSaving ||
                        (addedReports.length === 0 &&
                          removedReports.length === 0)
                      }
                    >
                      <Save sx={{ fontSize: 18, paddingRight: 1 }} />
                      {isSaving ? 'Saving...' : 'Save'}
                    </Button>
                  </Grid>
                </Grid>
              ) : (
                <Grid item xs={12}>
                  <Button
                    variant="contained"
                    size={'small'}
                    onClick={() => setEditReports(true)}
                  >
                    <Edit sx={{ fontSize: 18, paddingRight: 1 }} />
                    Edit Reports
                  </Button>
                </Grid>
              )}
              {errorSaving && editReports && (
                <Grid item xs={12} padding>
                  <Typography variant="body1" color={'error'}>
                    {errorSaving}
                  </Typography>
                </Grid>
              )}
            </CardActions>
          )}
        </Card>
      </Grid>
      <Grid item xs={12} md={6} padding>
        <Card sx={{ p: 2, boxShadow: 3, borderRadius: 2 }}>
          <CardHeader title={`Local Chapters`} sx={{ p: 1 }} />
          <CardContent sx={{ p: 1 }}>
            <Grid container>
              {userPermissions.local_chapters.map((lc) => (
                <Grid
                  item
                  xs={12}
                  key={lc.id}
                  backgroundColor={
                    removedLocalChapters.includes(lc.local_chapter_id)
                      ? '#f44336'
                      : 'inherit'
                  }
                >
                  <Typography variant="body1" component={'span'}>
                    {lc.name}
                    {canEdit() &&
                      editLocalChapters &&
                      !!!removedLocalChapters.includes(lc.local_chapter_id) && (
                        <IconButton
                          size={'small'}
                          color={'error'}
                          onClick={() => {
                            setRemovedLocalChapters([
                              ...removedLocalChapters,
                              lc.local_chapter_id,
                            ]);
                          }}
                        >
                          <Delete sx={{ fontSize: 18 }} />
                        </IconButton>
                      )}
                  </Typography>
                </Grid>
              ))}
              {userPermissions.local_chapters.length === 0 && (
                <Grid item xs={12} padding textAlign="center">
                  <Typography variant="body1">No local chapters</Typography>
                </Grid>
              )}
              {addedLocalChapterNames.map((lc) => (
                <Grid item xs={12} padding key={lc} backgroundColor={'#4caf50'}>
                  <Typography variant="body1" component={'span'}>
                    {lc}
                  </Typography>
                </Grid>
              ))}
              {editLocalChapters && (
                <Grid item container xs={12}>
                  <Grid item xs={10} paddingTop={1}>
                    <ChapterSelector
                      chapter={localChapterId}
                      handleChapterSelect={(localChapter) =>
                        handleSelectLocalChapter(localChapter)
                      }
                      keyId={'local-chapter'}
                      objectReturned={true}
                    />
                  </Grid>
                  {localChapterId && (
                    <Grid item xs={2} padding={1} textAlign={'center'}>
                      <IconButton
                        size={'small'}
                        onClick={() => {
                          handleAddLocalChapter();
                        }}
                        disabled={isSaving}
                        color={'green'}
                      >
                        <AddCircle />
                      </IconButton>
                    </Grid>
                  )}
                </Grid>
              )}
            </Grid>
          </CardContent>
          {canEdit() && (
            <CardActions>
              {editLocalChapters ? (
                <Grid container>
                  <Grid item xs={12}>
                    <Button
                      sx={{ marginRight: 1, fontWeight: 'bold' }}
                      variant="contained"
                      size={'small'}
                      color={'warning'}
                      onClick={cancelLocalChapterEdit}
                      disabled={isSaving}
                    >
                      Cancel
                    </Button>
                    <Button
                      sx={{ marginRight: 1, fontWeight: 'bold' }}
                      variant="contained"
                      size={'small'}
                      color={'green'}
                      onClick={handleSaveLocalChapterEdit}
                      disabled={
                        isSaving ||
                        (addedLocalChapters.length === 0 &&
                          removedLocalChapters.length === 0)
                      }
                    >
                      <Save sx={{ fontSize: 18, paddingRight: 1 }} />
                      {isSaving ? 'Saving...' : 'Save'}
                    </Button>
                  </Grid>
                </Grid>
              ) : (
                <Grid item xs={12}>
                  <Button
                    variant="contained"
                    size={'small'}
                    onClick={() => setEditLocalChapters(true)}
                  >
                    <Edit sx={{ fontSize: 18, paddingRight: 1 }} />
                    Edit Local Chapters
                  </Button>
                </Grid>
              )}
              {errorSaving && editLocalChapters && (
                <Grid item xs={12} padding>
                  <Typography variant="body1" color={'error'}>
                    {errorSaving}
                  </Typography>
                </Grid>
              )}
            </CardActions>
          )}
        </Card>
      </Grid>
      <Grid item xs={12} md={6} padding>
        <Card sx={{ p: 2, boxShadow: 3, borderRadius: 2 }}>
          <CardHeader title={`SPD Chapters`} sx={{ p: 1 }} />
          <CardContent sx={{ p: 1 }}>
            <Grid container>
              {userPermissions.spd_chapters.map((spd) => (
                <Grid
                  item
                  xs={12}
                  key={spd.id}
                  backgroundColor={
                    removedSPDChapters.includes(spd.spd_chapter_id)
                      ? '#f44336'
                      : 'inherit'
                  }
                >
                  <Typography variant="body1" component={'span'}>
                    {spd.name}
                    {canEdit() &&
                      editSPDChapters &&
                      !!!removedSPDChapters.includes(spd.spd_chapter_id) && (
                        <IconButton
                          size={'small'}
                          color={'error'}
                          onClick={() => {
                            setRemovedSPDChapters([
                              ...removedSPDChapters,
                              spd.spd_chapter_id,
                            ]);
                          }}
                        >
                          <Delete sx={{ fontSize: 18 }} />
                        </IconButton>
                      )}
                  </Typography>
                </Grid>
              ))}
              {userPermissions.spd_chapters.length === 0 && (
                <Grid item xs={12} padding textAlign="center">
                  <Typography variant="body1">No SPD chapters</Typography>
                </Grid>
              )}
              {addedSPDChapterNames.map((spd) => (
                <Grid
                  item
                  xs={12}
                  padding
                  key={spd}
                  backgroundColor={'#4caf50'}
                >
                  <Typography variant="body1" component={'span'}>
                    {spd}
                  </Typography>
                </Grid>
              ))}
            </Grid>
            {editSPDChapters && (
              <Grid item container xs={12}>
                <Grid item xs={10} paddingTop={1}>
                  <SPDSelector
                    spd={spdChapterId}
                    handleSpdSelect={(spdObj) => handleSelectSpdChapter(spdObj)}
                    keyId={'spd-chapter'}
                    all={true}
                    objectReturned={true}
                  />
                </Grid>
                {spdChapterId && (
                  <Grid item xs={2} padding={1} textAlign={'center'}>
                    <IconButton
                      size={'small'}
                      onClick={() => {
                        handleAddSpdChapter();
                      }}
                      disabled={isSaving}
                      color={'green'}
                    >
                      <AddCircle />
                    </IconButton>
                  </Grid>
                )}
              </Grid>
            )}
          </CardContent>
          {canEdit() && (
            <CardActions>
              {editSPDChapters ? (
                <Grid container>
                  <Grid item xs={12}>
                    <Button
                      sx={{ marginRight: 1, fontWeight: 'bold' }}
                      variant="contained"
                      size={'small'}
                      color={'warning'}
                      onClick={cancelSpdChapterEdit}
                      disabled={isSaving}
                    >
                      Cancel
                    </Button>
                    <Button
                      sx={{ marginRight: 1, fontWeight: 'bold' }}
                      variant="contained"
                      size={'small'}
                      color={'green'}
                      onClick={handleSaveSpdChapterEdit}
                      disabled={
                        isSaving ||
                        (addedSPDChapters.length === 0 &&
                          removedSPDChapters.length === 0)
                      }
                    >
                      <Save sx={{ fontSize: 18, paddingRight: 1 }} />
                      {isSaving ? 'Saving...' : 'Save'}
                    </Button>
                  </Grid>
                </Grid>
              ) : (
                <Grid item xs={12}>
                  <Button
                    variant="contained"
                    size={'small'}
                    onClick={() => setEditSPDChapters(true)}
                  >
                    <Edit sx={{ fontSize: 18, paddingRight: 1 }} />
                    Edit SPD Chapters
                  </Button>
                </Grid>
              )}
              {errorSaving && editSPDChapters && (
                <Grid item xs={12} padding>
                  <Typography variant="body1" color={'error'}>
                    {errorSaving}
                  </Typography>
                </Grid>
              )}
            </CardActions>
          )}
        </Card>
      </Grid>
    </Grid>
  );
}
